// src/resources/dashboards/ProductEvolution/sections/OverviewSection.tsx
//
// Cabeçalho comercial da rede + KPIs com delta + série no tempo.
//
// Litros por abastecimento e R$/litro ficam ao lado do ticket de propósito: sem
// eles, alta de preço do combustível é lida como ganho de produto. O quintil
// diz onde a rede está DENTRO da carteira — número absoluto sozinho não decide
// nada numa conversa comercial.
import React from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import {
  COLORS, DeltaChip, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum,
} from '../../shared';
import type { SectionState } from '../../shared';
import { ChainOverviewData } from '../types';
import {
  BackendNote, Card, StatGrid, bucketLabel, fmtBrlPrecise, fmtDate, quintileText,
} from './kit';

const HEIGHT = 470;

const Kpi = ({ label, value, deltaPct, sub, hint, accent }: {
  label: string;
  value: string;
  deltaPct: number | null;
  sub?: string;
  hint?: string;
  accent?: string;
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.faint }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 23, fontWeight: 800, color: accent ?? COLORS.ink, lineHeight: 1.25 }}>{value}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <DeltaChip deltaPct={deltaPct} />
      {sub && <Typography sx={{ fontSize: 11, color: COLORS.muted }}>{sub}</Typography>}
    </Box>
    {hint && <Typography sx={{ fontSize: 11, color: COLORS.faint }}>{hint}</Typography>}
  </Box>
);

const OverviewSection = ({ state }: { state: SectionState<ChainOverviewData> }) => {
  const theme = useTheme();
  // Tríptico. Stale-while-revalidate: com `data` na mão, trocar o período NÃO
  // volta pro skeleton — o card anterior fica até a resposta nova chegar.
  if (state.loading && !state.data) return <SkeletonCard height={HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={HEIGHT} />;
  if (!state.data) return null;

  const { chain, kpis, series, bucketUnit, dataThrough, avgTicketQuintile, portfolioAvgTicketMedian } = state.data;
  // Recharts herda a fonte do <body>, não a do tema — passamos à mão.
  const chartFont = theme.typography.fontFamily;

  const chartData = series.map(b => ({
    label: bucketLabel(b.bucket, bucketUnit),
    abastecimentos: b.fillins,
    ticket: Number(b.avgTicket.toFixed(2)),
    receita: b.revenue,
    litros: b.liters,
  }));

  const quintile = quintileText(avgTicketQuintile);

  return (
    <Box>
      <SectionHeader
        icon={<InsightsIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.brand2}, ${COLORS.brand})`}
        title="Visão geral da rede"
        subtitle="Ticket, receita e volume no período, comparados com o período anterior"
      />

      <Box sx={{ display: 'grid', gap: 1.75 }}>
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 19, fontWeight: 800, color: COLORS.ink }}>{chain.name}</Typography>
            {chain.canceledAt ? (
              <Chip size="small" label={`Cancelada em ${fmtDate(chain.canceledAt)}`}
                sx={{ height: 21, fontSize: 10.5, fontWeight: 800, color: '#fff', bgcolor: COLORS.danger }} />
            ) : (
              <Chip size="small" label={chain.isActive ? 'Ativa' : 'Inativa'}
                sx={{
                  height: 21, fontSize: 10.5, fontWeight: 800,
                  color: chain.isActive ? COLORS.success : COLORS.faint,
                  bgcolor: chain.isActive ? 'rgba(34,197,94,0.14)' : 'rgba(148,163,184,0.18)',
                }} />
            )}
          </Box>
          {chain.cancelReason && (
            <Typography sx={{ fontSize: 12, color: COLORS.danger, mt: 0.25 }}>Motivo: {chain.cancelReason}</Typography>
          )}
          <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 0.5, lineHeight: 1.7 }}>
            <b>{fmtNum(chain.places.active)}</b> de <b>{fmtNum(chain.places.total)}</b> postos ativos
            {' · '}MRR <b>{fmtBrl(chain.mrr)}</b>
            {' · '}cadastrada em <b>{fmtDate(chain.createdAt)}</b>
            {chain.firstBillingStartDate ? <> · 1º faturamento em <b>{fmtDate(chain.firstBillingStartDate)}</b></> : null}
          </Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.7 }}>
            {chain.retailers.length === 0
              ? 'Sem revenda vinculada'
              : chain.retailers.length === 1
                ? <>Revenda: <b>{chain.retailers[0].name}</b></>
                : (
                  <>
                    Revendas ({chain.retailers.length}):{' '}
                    <b>{chain.retailers.map(r => `${r.name} (${fmtNum(r.places)})`).join(' · ')}</b>
                  </>
                )}
          </Typography>
          {dataThrough && (
            <BackendNote>Volume agregado até {fmtDate(dataThrough)} · o dia corrente entra ao vivo.</BackendNote>
          )}
        </Box>

        <Box sx={cardSx}>
          <StatGrid min={158}>
            <Kpi label="Ticket médio" value={fmtBrl(kpis.avgTicket.value)} deltaPct={kpis.avgTicket.deltaPct}
              sub={`antes ${fmtBrl(kpis.avgTicket.previous)}`} accent={COLORS.brand} />
            <Kpi label="Receita no período" value={fmtBrl(kpis.revenue.value)} deltaPct={kpis.revenue.deltaPct}
              sub={`antes ${fmtBrl(kpis.revenue.previous)}`} />
            <Kpi label="Abastecimentos" value={fmtNum(kpis.fillins.value)} deltaPct={kpis.fillins.deltaPct}
              sub={`antes ${fmtNum(kpis.fillins.previous)}`} accent={COLORS.blue} />
            <Kpi label="Litros" value={`${fmtNum(kpis.liters.value)} L`} deltaPct={kpis.liters.deltaPct}
              sub={`antes ${fmtNum(kpis.liters.previous)} L`} />
            <Kpi label="Litros / abastecimento" value={`${fmtNum(kpis.litersPerFillin.value, 1)} L`}
              deltaPct={kpis.litersPerFillin.deltaPct} sub={`antes ${fmtNum(kpis.litersPerFillin.previous, 1)} L`} />
            <Kpi label="R$ por litro" value={fmtBrlPrecise(kpis.pricePerLiter.value)}
              deltaPct={kpis.pricePerLiter.deltaPct} sub={`antes ${fmtBrlPrecise(kpis.pricePerLiter.previous)}`}
              hint="Ticket sobe com preço de combustível: olhe os três juntos." />
          </StatGrid>

          {/* Contexto de carteira: o ticket absoluto não diz se a rede vai bem. */}
          <Box sx={{
            mt: 2, px: 1.75, py: 1.25, borderRadius: 3,
            bgcolor: 'rgba(120,105,223,0.06)', border: '1px solid rgba(120,105,223,0.16)',
          }}>
            <Typography sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.6 }}>
              {quintile
                ? <>Esta rede está no <b>{quintile}</b> (1 = pior, 5 = melhor).</>
                : <>Sem amostra suficiente na carteira para posicionar o ticket desta rede em quintil.</>}
              {portfolioAvgTicketMedian !== null && (
                <> Mediana da carteira no mesmo período: <b>{fmtBrl(portfolioAvgTicketMedian)}</b>.</>
              )}
            </Typography>
          </Box>
        </Box>

        <Card
          title="Evolução no período"
          hint={`Abastecimentos e ticket médio por ${bucketUnit === 'week' ? 'semana' : 'dia'}`}
        >
          {!chartData.length ? (
            <EmptyHint text="Nenhum abastecimento agregado neste período." />
          ) : (
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }}
                    tickLine={false} axisLine={{ stroke: 'rgba(148,163,184,0.35)' }} minTickGap={16} />
                  <YAxis yAxisId="left" tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }}
                    tickLine={false} axisLine={false} width={52} tickFormatter={(v: number) => fmtNum(v)} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.brand }}
                    tickLine={false} axisLine={false} width={62} tickFormatter={(v: number) => fmtBrl(v)} />
                  <ReTooltip
                    cursor={{ fill: 'rgba(120,105,223,0.06)' }}
                    contentStyle={{ fontFamily: chartFont, fontSize: 12, borderRadius: 8, border: '1px solid rgba(120,105,223,0.2)' }}
                    formatter={(value: any, name: any) => (
                      name === 'Ticket médio' ? [fmtBrl(Number(value)), name] : [fmtNum(Number(value)), name]
                    )}
                  />
                  <Legend wrapperStyle={{ fontFamily: chartFont, fontSize: 11 }} iconSize={8} />
                  <Bar yAxisId="left" dataKey="abastecimentos" name="Abastecimentos" fill={COLORS.blue} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Line yAxisId="right" dataKey="ticket" name="Ticket médio" stroke={COLORS.brand} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default OverviewSection;
